import {Notification} from '@/components-v2/molecules/Notification/Notification'
import {useEffect, useState} from 'react'

/**
 * Toast-style alert built on the v2 Notification component. Auto-closes after 5 seconds
 * unless its details list is expanded, so an error breakdown doesn't disappear mid-read.
 *
 * @param {Object} props
 * @param {'success' | 'error' | 'info' | 'warning'} props.type Notification visual type.
 * @param {string} props.message Message shown as the notification title.
 * @param {string[]} [props.details] Optional list of detail messages, revealed via a "View details" toggle.
 * @param {boolean} [props.show=false] Whether the alert is visible or not.
 * @param {Function} props.onCloseClick Called when the alert should be dismissed.
 *
 * @returns {JSX.Element|null}
 */
export const Alert = ({type, message, details, show = false, onCloseClick}) => {
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false)

  useEffect(() => {
    setIsDetailsExpanded(false)
  }, [message])

  useEffect(() => {
    // Pause auto-close while details are expanded so the message doesn't disappear mid-read.
    if (!show || isDetailsExpanded) {
      return undefined
    }

    const itv = setTimeout(() => onCloseClick?.(), 5000)
    return () => clearTimeout(itv)
  }, [message, onCloseClick, show, type, isDetailsExpanded])

  if (!show) {
    return null
  }

  return (
    <Notification
      className="alert absolute right-4 top-6 z-40 w-full max-w-[calc(100vw-7.5rem)]"
      data-testid={`alert-${type}`}
      role="alert"
      type={type}
      title={message || ''}
      details={details}
      onDetailsToggle={setIsDetailsExpanded}
    />
  )
}
